import { isRecaptchaEnabled, RECAPTCHA_ERROR, type RecaptchaAction } from "@/lib/recaptcha";

export type RecaptchaClient = {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
    interface Window { grecaptcha?: { enterprise?: RecaptchaClient }; }
}

/** Wait for the shared script, then generate a fresh token before each submission. */
export function getRecaptchaToken(action: RecaptchaAction): Promise<string> {
    if (!isRecaptchaEnabled()) return Promise.resolve("");
    const siteKey = process.env["NEXT_PUBLIC_RECAPTCHA_SITE_KEY"];
    if (typeof window === "undefined" || !siteKey) return Promise.reject(new Error(RECAPTCHA_ERROR));
    return new Promise((resolve, reject) => {
        let settled = false;
        let poll: ReturnType<typeof setTimeout> | undefined;
        const finish = (token?: string) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            clearTimeout(poll);
            if (token?.trim()) resolve(token);
            else reject(new Error(RECAPTCHA_ERROR));
        };
        const timeout = setTimeout(() => finish(), 10000);
        const waitForScript = () => {
            if (settled) return;
            const client = window.grecaptcha?.enterprise;
            if (!client?.ready || !client.execute) {
                poll = setTimeout(waitForScript, 50);
                return;
            }
            try {
                client.ready(() => {
                    if (settled) return;
                    try {
                        client.execute(siteKey, { action }).then(token => finish(token), () => finish());
                    } catch { finish(); }
                });
            } catch { finish(); }
        };
        waitForScript();
    });
}

/** Keep token acquisition inside React's pending action, with a recoverable form error. */
export function withRecaptcha<State, Result>(
    action: RecaptchaAction,
    submit: (state: State, data: FormData) => Promise<Result>,
    failure: () => Result
): (state: State, data: FormData) => Promise<Result> {
    return async (state, data) => {
        try {
            data.set("recaptchaToken", await getRecaptchaToken(action));
        } catch {
            return failure();
        }
        return submit(state, data);
    };
}
